// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { ArrowPosition, ArrowType, SwiperInstance } from "@west/App/Components/XngCarousel/XngCarousel";
import ChevronLeft from "@west/XDL/Icons/ChevronLeft";
import ChevronRight from "@west/XDL/Icons/ChevronRight";
import { xClassNames } from "@west/XDL/Utilities";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";

export const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 16,
    },
    bottom: {
        src: ArrowDown,
        size: 16,
    },
    left: {
        src: ChevronLeft,
        size: 16,
    },
    right: {
        src: ChevronRight,
        size: 16,
    },
};

export const getArrowPosition = (
    arrowType: ArrowType,
    direction: "horizontal" | "vertical" = "horizontal",
): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    // when arrowType='prev'.
    return isVertical ? "top" : "left";
};

export function useArrowNavigation({ swiper, arrowType }: { swiper: SwiperInstance | null; arrowType: string }) {
    const [options, setOptions] = useState<{ className?: string; disabled?: boolean; element?: HTMLElement }>({
        className: "",
        element: undefined,
        disabled: true,
    });

    const debouncedUpdateArrowOptions = useCallback(
        debounce((swiper: SwiperInstance) => {
            updateArrowOptions(swiper);
        }, 100),
        [],
    );

    const updateArrowOptions = (swiper: SwiperInstance) => {
        if (!swiper?.navigation) {
            return;
        }
        
        const arrowElement = arrowType === "next" ? swiper?.navigation.nextEl : swiper?.navigation.prevEl;
        if (!arrowElement) {
            return;
        }

        const classNames = [...arrowElement.classList].filter(className => className !== `swiper-button-${arrowType}`);
        const isDisabled = classNames.includes("swiper-button-disabled");

        setOptions({
            className: xClassNames.append([...classNames]),
            disabled: isDisabled,
            element: arrowElement,
        });
    };

    useEffect(() => {
        if (!swiper) {
            return;
        }

        swiper.on("slideChange", debouncedUpdateArrowOptions);
        swiper.on("_slideClasses", debouncedUpdateArrowOptions);
        updateArrowOptions(swiper);

        return () => {
            swiper.off("slideChange", debouncedUpdateArrowOptions);
            swiper.off("_slideClasses", debouncedUpdateArrowOptions);
        };
    }, [swiper, debouncedUpdateArrowOptions]);

    return options;
}
